/**
 * sl-m82f.4.2: Generic loader hook for tab data.
 *
 * Each feature tab fetches its own detail rows (per-doc, per-bot, per-user,
 * per-workflow). Instead of repeating the same `useState + useEffect +
 * error handling` in five tabs, this hook owns the pattern once — every
 * tab calls it with its own fetcher and a cache key.
 *
 * sl-m82f.4b: Unified load path — both the auto-load on key change and
 * the manual `refetch()` go through the same cancellation-aware runner,
 * eliminating the previous DRY violation and the race condition where a
 * fast `refetch()` could overwrite a newer response.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncResourceResult<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Runs `fetcher` whenever `key` changes. Cancels stale responses so a
 * quick tab-switch can't overwrite fresh data with a slow earlier response.
 */
export function useAsyncResource<T>(
  key: string,
  fetcher: () => Promise<T>
): AsyncResourceResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Monotonic counter: every new load increments it. If the counter has
  // moved on by the time the promise settles, the result is stale.
  const versionRef = useRef(0);

  // Stable ref to always close over the latest fetcher without triggering
  // re-renders when it changes identity.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(() => {
    const version = ++versionRef.current;
    setIsLoading(true);
    setError(null);
    void (async () => {
      try {
        const result = await fetcherRef.current();
        if (versionRef.current === version) setData(result);
      } catch (caught) {
        if (versionRef.current !== version) return;
        const message =
          caught instanceof Error ? caught.message : 'Failed to load data';
        setError(message);
        setData(null);
      } finally {
        if (versionRef.current === version) setIsLoading(false);
      }
    })();
  }, []);

  // Auto-load whenever the cache key changes.
  useEffect(() => {
    run();
    // `run` is stable (no deps). `key` drives re-execution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { data, isLoading, error, refetch: run };
}
